/**
 * Project Filter — views/default/post-types/page/layouts/project_filter.php
 *
 * - The filter buttons (`data-project-filter-term`: a `project-category`
 *   slug, or '' for All) rebuild the carousel's slides with the projects in
 *   that category (`data-terms` on each card) and return it to the start;
 *   `aria-pressed` follows and a polite live region reports the count.
 * - The change is animated on the cards' links only (never the slides or the
 *   wrapper, whose sizes and transform Swiper owns): the cards in view leave
 *   (`is-leaving`, staggered), the slides are swapped instantly, then the new
 *   cards in view enter (`is-entering` removed, staggered via
 *   `--project-card-index`). A click during an animation cancels it and goes
 *   straight to the new filter; with `prefers-reduced-motion` the swap is
 *   instant. Timings match _project_filter.scss.
 * - The cards are read once, before any filtering, and re-added from that
 *   list, so every filter starts from all projects in their original order.
 * - The Swiper (slideshow-manager.js) is looked up on each change, so the
 *   filter works whether or not it has initialised yet; without one, the
 *   cards are swapped in the list directly.
 */

const controllers = new Set();

// Leave: 200ms, 40ms apart; enter: 300ms, 60ms apart (_project_filter.scss).
const LEAVE_MS = 200;
const LEAVE_STAGGER_MS = 40;
const ENTER_MS = 300;
const ENTER_STAGGER_MS = 60;

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Cards whose box overlaps the carousel's visible area, in order.
 *
 * @param {HTMLElement[]} cards
 * @param {HTMLElement} carousel
 * @returns {HTMLElement[]}
 */
function cardsInView(cards, carousel) {
    const view = carousel.getBoundingClientRect();
    const right = Math.min(view.right, window.innerWidth);
    return cards.filter((card) => {
        const box = card.getBoundingClientRect();
        return box.right > view.left && box.left < right;
    });
}

/**
 * Give each card its stagger position (or clear it).
 *
 * @param {HTMLElement[]} cards
 * @param {boolean} [clear]
 */
function setIndexes(cards, clear = false) {
    cards.forEach((card, index) => {
        if (clear) {
            card.style.removeProperty('--project-card-index');
        } else {
            card.style.setProperty('--project-card-index', index);
        }
    });
}

/**
 * @param {HTMLElement} root `[data-project-filter]`
 * @returns {AbortController}
 */
function setupProjectFilter(root) {
    const abort = new AbortController();
    const { signal } = abort;
    const carousel = root.querySelector('.project-filter__carousel');
    const list = root.querySelector('.project-filter__list');
    const buttons = [...root.querySelectorAll('[data-project-filter-term]')];
    const status = root.querySelector('[data-project-filter-status]');
    const cards = [...list.children];
    let run = 0;
    let timer = null;

    const wait = (ms, token) => new Promise((resolve) => {
        timer = window.setTimeout(() => resolve(token === run && !signal.aborted), ms);
    });

    // Drop any animation state left by a cancelled change.
    const reset = () => {
        window.clearTimeout(timer);
        cards.forEach((card) => card.classList.remove('is-leaving', 'is-entering'));
        setIndexes(cards, true);
    };

    const swap = (matches) => {
        const swiper = carousel?.swiper;
        if (swiper && !swiper.destroyed) {
            swiper.removeAllSlides();
            swiper.appendSlide(matches);
            swiper.update();
            swiper.slideTo(0, 0);
        } else {
            list.replaceChildren(...matches);
        }
    };

    const filter = async (term) => {
        const token = ++run;
        const matches = term
            ? cards.filter((card) => (card.dataset.terms || '').split(' ').includes(term))
            : cards;

        buttons.forEach((button) => {
            button.setAttribute('aria-pressed', String(button.dataset.projectFilterTerm === term));
        });
        if (status) {
            status.textContent = `${matches.length} ${matches.length === 1 ? 'project' : 'projects'}`;
        }

        reset();

        if (reducedMotion()) {
            swap(matches);
            return;
        }

        // Out: the cards in view, staggered.
        const leaving = cardsInView([...list.children], carousel);
        setIndexes(leaving);
        leaving.forEach((card) => card.classList.add('is-leaving'));
        if (leaving.length && !(await wait(LEAVE_MS + LEAVE_STAGGER_MS * (leaving.length - 1), token))) return;

        // Swap, with the new cards hidden.
        reset();
        matches.forEach((card) => card.classList.add('is-entering'));
        swap(matches);

        // In: the cards now in view, staggered; the rest appear at once.
        const entering = cardsInView(matches, carousel);
        matches.filter((card) => !entering.includes(card)).forEach((card) => card.classList.remove('is-entering'));
        setIndexes(entering);
        void list.offsetWidth; // Commit the hidden state before animating.
        entering.forEach((card) => card.classList.remove('is-entering'));

        if (await wait(ENTER_MS + ENTER_STAGGER_MS * Math.max(entering.length - 1, 0), token)) {
            setIndexes(entering, true);
        }
    };

    buttons.forEach((button) => {
        button.addEventListener('click', () => {
            if (button.getAttribute('aria-pressed') === 'true') return;
            filter(button.dataset.projectFilterTerm);
        }, { signal });
    });

    signal.addEventListener('abort', () => window.clearTimeout(timer));

    return abort;
}

/**
 * @param {ParentNode} [container=document]
 */
export function initializeProjectFilters(container = document) {
    container.querySelectorAll('[data-project-filter]').forEach((root) => {
        controllers.add(setupProjectFilter(root));
    });
}

export function destroyProjectFilters() {
    controllers.forEach((controller) => controller.abort());
    controllers.clear();
}
